import React from 'react';
import {
  DndContext, PointerSensor, KeyboardSensor, closestCenter,
  useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Clock, GripVertical, History, MessageSquare } from 'lucide-react';

const GREEN = '#0F6E56';

function formatMinutes(minutes) {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

/**
 * Single sortable task row. Drag handle is rendered only when `canDrag` is
 * true (admins/consultants on non-cancelled tasks). All click-action props
 * are wired exactly like the inline implementation that lived in
 * ProjectDetails.js — so behaviour is identical, just reorderable.
 */
function SortableTaskRow({
  task, canEdit, canDrag, getStatusColor, getStatusLabel,
  onStatusChange, onComments, onHistory, onEdit, onCancel, onTime,
  timeSummary,
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: task.task_id, disabled: !canDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    boxShadow: isDragging ? '0 8px 22px rgba(0,0,0,0.12)' : undefined,
    zIndex: isDragging ? 10 : 'auto',
  };
  const cancelled = task.status === 'cancelled';

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 rounded-lg ${
        cancelled ? 'bg-gray-100 opacity-60' : 'bg-gray-50'
      }`}
      data-testid={`task-${task.task_id}`}
    >
      <div className="flex items-center space-x-3 min-w-0">
        {canDrag ? (
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="text-gray-400 hover:text-gray-700 cursor-grab active:cursor-grabbing p-1 -ml-1 rounded touch-none"
            aria-label="Arrastar para reordenar"
            title="Arrastar para reordenar"
            data-testid={`drag-handle-${task.task_id}`}
          >
            <GripVertical className="w-4 h-4" />
          </button>
        ) : (
          <span className="w-4 inline-block" />
        )}

        <Badge className={getStatusColor(task.status)}>{getStatusLabel(task.status)}</Badge>
        <span className={`font-medium ${cancelled ? 'line-through text-gray-500' : 'text-gray-900'}`}>
          {task.name}
        </span>
        <span className="text-sm text-gray-500">({task.weight}%)</span>
        {task.deadline && (
          <span className="text-sm text-gray-500">
            Prazo: {new Date(task.deadline).toLocaleDateString('pt-BR')}
          </span>
        )}
        {!task.client_visible && (
          <Badge variant="outline" className="text-xs">Oculta</Badge>
        )}
        {timeSummary?.total_minutes ? (
          <span
            className="text-xs font-medium inline-flex items-center gap-1"
            style={{ color: GREEN }}
            title={`${timeSummary.entry_count} lançamento(s)`}
            data-testid={`task-time-badge-${task.task_id}`}
          >
            <Clock className="w-3 h-3" />
            {formatMinutes(timeSummary.total_minutes)}
          </span>
        ) : null}
      </div>

      {canEdit && !cancelled && (
        <div className="flex items-center space-x-2 shrink-0">
          <Select
            value={task.status}
            onValueChange={(value) => onStatusChange(task.task_id, value)}
          >
            <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="not_started">Não Iniciada</SelectItem>
              <SelectItem value="in_progress">Em Andamento</SelectItem>
              <SelectItem value="completed">Concluída</SelectItem>
            </SelectContent>
          </Select>

          <Button
            size="sm" variant="outline"
            onClick={() => onComments(task)}
            data-testid={`comments-task-${task.task_id}`}
            title="Comentários"
          >
            <MessageSquare className="w-4 h-4" />
          </Button>

          <Button
            size="sm" variant="outline"
            onClick={() => onTime(task)}
            data-testid={`time-task-${task.task_id}`}
            title="Registrar horas"
          >
            <Clock className="w-4 h-4" />
          </Button>

          <Button
            size="sm" variant="outline"
            onClick={() => onHistory(task)}
            data-testid={`history-task-${task.task_id}`}
            title="Ver histórico"
          >
            <History className="w-4 h-4" />
          </Button>

          <Button
            size="sm" variant="outline"
            onClick={() => onEdit(task)}
            data-testid={`edit-task-${task.task_id}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </Button>

          <Button
            size="sm" variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => onCancel(task.task_id)}
            data-testid={`cancel-task-${task.task_id}`}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </Button>
        </div>
      )}
    </div>
  );
}

/**
 * Drag-and-drop ordered list of tasks within a single phase.
 * Props mirror the action handlers used by ProjectDetails.js.
 *
 *  - `tasks`: array of task objects (already filtered to this phase)
 *  - `onReorder(phaseId, newTaskIds)`: called after a successful drop;
 *     the parent owns persistence + state refresh.
 */
export default function SortableTaskList({
  tasks, phaseId, canEdit,
  getStatusColor, getStatusLabel,
  onStatusChange, onComments, onHistory, onEdit, onCancel, onReorder,
  onTime, timeSummary,
}) {
  const sensors = useSensors(
    // 6px movement before drag starts so single clicks still register on
    // the drag handle button (it doesn't have other click behaviour).
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = tasks.findIndex((t) => t.task_id === active.id);
    const newIndex = tasks.findIndex((t) => t.task_id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const reordered = arrayMove(tasks, oldIndex, newIndex);
    onReorder(phaseId, reordered.map((t) => t.task_id));
  };

  const ids = tasks.map((t) => t.task_id);

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div className="space-y-2" data-testid={`sortable-tasks-${phaseId}`}>
          {tasks.map((task) => (
            <SortableTaskRow
              key={task.task_id}
              task={task}
              canEdit={canEdit}
              canDrag={canEdit && task.status !== 'cancelled'}
              getStatusColor={getStatusColor}
              getStatusLabel={getStatusLabel}
              onStatusChange={onStatusChange}
              onComments={onComments}
              onHistory={onHistory}
              onEdit={onEdit}
              onCancel={onCancel}
              onTime={onTime}
              timeSummary={timeSummary?.[task.task_id]}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
